import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Mail, MapPin, Phone, Facebook, Instagram, Linkedin, Youtube,
    LogIn, ArrowRight,
} from "lucide-react";
import { HEAD_OFFICE, BRANCHES, CITIES, SOCIAL } from "../data/siteData";
import { EXTENDED_CITIES } from "../data/extendedCities";
import { COUNTRIES_DATA } from "../data/countriesData";
import { useSettings } from "../context/SettingsContext";
import { usePagePhone } from "../hooks/usePagePhone";
import { api } from "../lib/api";
import BookMyTutorPromo from "./BookMyTutorPromo";
import Logo from "./Logo";

// Column link → tight, hover-amber, single-line
function FLink({ to, children, testId }) {
    return (
        <li>
            <Link
                to={to}
                data-testid={testId}
                className="text-slate-400 hover:text-amber-400 transition-colors"
            >
                {children}
            </Link>
        </li>
    );
}

// Small SEO pill for the flat "All cities / countries" row
function Pill({ to, children, testId }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-800/60 hover:bg-slate-700 text-[11px] text-slate-400 hover:text-amber-300 transition-colors whitespace-nowrap"
        >
            {children}
        </Link>
    );
}

export default function Footer() {
    const { settings } = useSettings();
    const [tutorLoginVisible, setTutorLoginVisible] = useState(false);
    useEffect(() => {
        let cancelled = false;
        api.get("/tutor/portal/status")
            .then((r) => { if (!cancelled) setTutorLoginVisible(Boolean(r.data?.login_button_visible)); })
            .catch(() => { /* stay hidden */ });
        return () => { cancelled = true; };
    }, []);
    const socialUrls = {
        facebook: settings.facebook_url || SOCIAL.facebook,
        instagram: settings.instagram_url || SOCIAL.instagram,
        linkedin: settings.linkedin_url || SOCIAL.linkedin,
        youtube: settings.youtube_url || SOCIAL.youtube,
    };
    const pagePhone = usePagePhone();
    const ho = {
        ...HEAD_OFFICE,
        // V97 — Footer phone is strictly route-aware. Each landing page shows
        // ONLY its assigned contact number (Jabalpur / Bhopal / Nagpur /
        // Makeover / Pan-India). Admin's global head_office_phone override
        // is intentionally NOT applied to city / makeover pages so the user
        // sees the right local number consistently across header + footer.
        phones: [pagePhone.display],
        email: settings.head_office_email || HEAD_OFFICE.email,
        address: settings.head_office_address || HEAD_OFFICE.address,
    };
    const socials = [
        { url: socialUrls.facebook, Icon: Facebook, label: "Facebook" },
        { url: socialUrls.instagram, Icon: Instagram, label: "Instagram" },
        { url: socialUrls.linkedin, Icon: Linkedin, label: "LinkedIn" },
        { url: socialUrls.youtube, Icon: Youtube, label: "YouTube" },
    ].filter((s) => s.url);

    return (
        <footer className="bg-[#0F172A] text-slate-300" data-testid="site-footer">
            {/* ═══ TIER 1 — Compact balanced 5-column layout ═══ */}
            <div className="max-w-[1440px] mx-auto px-6 lg:px-8 pt-12 pb-8 grid lg:grid-cols-5 md:grid-cols-2 gap-x-8 gap-y-10 items-start">

                {/* Col 1 — Brand + Head Office */}
                <div className="lg:col-span-1 space-y-4">
                    <Logo alt="Anand Tutorials logo" className="h-14 w-auto" />
                    <p className="text-xs leading-relaxed text-slate-400">
                        India&apos;s most trusted 1:1 home &amp; online tutoring since 2013 — Jabalpur, Bhopal, Nagpur, Pan-India &amp; worldwide.
                    </p>
                    <div className="space-y-2 text-xs" data-testid="footer-head-office">
                        <div className="flex gap-2 items-start">
                            <MapPin className="w-3.5 h-3.5 mt-0.5 text-amber-400 shrink-0" />
                            <span className="text-slate-400 leading-relaxed">{ho.address}</span>
                        </div>
                        {ho.phones.map((p) => (
                            <div key={p} className="flex gap-2 items-start">
                                <Phone className="w-3.5 h-3.5 mt-0.5 text-amber-400 shrink-0" />
                                <a href={`tel:${p.replace(/\s+/g, "")}`} className="text-slate-400 hover:text-amber-400">Call Us</a>
                            </div>
                        ))}
                        <div className="flex gap-2 items-start">
                            <Mail className="w-3.5 h-3.5 mt-0.5 text-amber-400 shrink-0" />
                            <div className="flex flex-col gap-0.5">
                                <a href={`mailto:${ho.email}`} className="text-slate-400 hover:text-amber-400 break-all" data-testid="footer-email-primary">{ho.email}</a>
                                <a href="mailto:info@anandtutorials.com" className="text-slate-400 hover:text-amber-400 break-all" data-testid="footer-email-business">info@anandtutorials.com</a>
                            </div>
                        </div>
                    </div>
                    {socials.length > 0 && (
                        <div className="flex gap-2 pt-1" data-testid="footer-social">
                            {socials.map(({ url, Icon, label }) => (
                                <a
                                    key={label}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 bg-slate-800 rounded-md hover:bg-[#1E40AF] transition-colors"
                                    aria-label={label}
                                    data-testid={`footer-social-${label.toLowerCase()}`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                </a>
                            ))}
                        </div>
                    )}

                    {/* Branch address cards — clean, readable on every viewport.
                        Displayed here (under the Head Office) so col 1 never
                        looks empty next to the taller link columns. */}
                    <div data-testid="footer-branches" className="pt-2 space-y-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Our Branches</div>
                        {BRANCHES.map((b) => {
                            const [labelPart, localityPart] = b.label.split("—").map((s) => s.trim());
                            return (
                                <div
                                    key={b.label}
                                    className="rounded-lg border border-slate-800 bg-slate-800/40 p-3"
                                    data-testid={`footer-branch-${(labelPart || b.label).toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-300">
                                            {labelPart}
                                            {localityPart && <span className="text-slate-400 font-medium normal-case tracking-normal"> · {localityPart}</span>}
                                        </div>
                                    </div>
                                    <div className="text-[11px] leading-relaxed text-slate-400 break-words">{b.address}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Col 2 — Company */}
                <div>
                    <h4 className="font-heading text-white font-semibold mb-3 text-xs uppercase tracking-[0.2em]">Company</h4>
                    <ul className="space-y-2 text-sm">
                        <FLink to="/about">About Us</FLink>
                        <FLink to="/founder">Founder</FLink>
                        <FLink to="/team">Team</FLink>
                        <FLink to="/how-it-works">How It Works</FLink>
                        <FLink to="/blog">Blog</FLink>
                        <FLink to="/faqs">FAQs</FLink>
                        <FLink to="/contact">Contact</FLink>
                        <FLink to="/become-a-tutor">Become a Tutor</FLink>
                        <FLink to="/tutor-guide" testId="footer-tutor-guide">📘 Tutor Guide</FLink>
                        <FLink to="/pay-now">Pay Now</FLink>
                        <FLink to="/sitemap" testId="footer-html-sitemap">Sitemap</FLink>
                    </ul>
                </div>

                {/* Col 3 — Services */}
                <div>
                    <h4 className="font-heading text-white font-semibold mb-3 text-xs uppercase tracking-[0.2em]">Services</h4>
                    <ul className="space-y-2 text-sm">
                        <FLink to="/online-classes">1:1 Online Classes</FLink>
                        <FLink to="/courses">Courses &amp; Boards</FLink>
                        <FLink to="/online-tuition-worldwide" testId="footer-online-worldwide">🌍 Online Tuition Worldwide</FLink>
                        <FLink to="/foreign-students">Foreign / NRI Students</FLink>
                        <FLink to="/best-home-tuition-provider-in-india">Best Home Tuition in India</FLink>
                        <FLink to="/best-online-tuition-platform-in-india">Best Online Tuition Platform</FLink>
                        <FLink to="/top-rated-home-tutors-in-india">Top Rated Home Tutors</FLink>
                        <FLink to="/why-anand-tutorials-is-trusted-across-india">Why Trusted Across India</FLink>
                    </ul>
                </div>

                {/* Col 4 — Locations */}
                <div>
                    <h4 className="font-heading text-white font-semibold mb-3 text-xs uppercase tracking-[0.2em]">Home Tuition</h4>
                    <ul className="space-y-2 text-sm">
                        {Object.values(CITIES).map((c) => (
                            <FLink key={c.slug} to={`/${c.slug}`}>Home Tuition in {c.name}</FLink>
                        ))}
                    </ul>
                    <h4 className="font-heading text-white font-semibold mt-5 mb-3 text-xs uppercase tracking-[0.2em]">Top Metros (Online)</h4>
                    <ul className="space-y-2 text-sm">
                        {Object.values(EXTENDED_CITIES).slice(0, 6).map((c) => (
                            <FLink key={c.slug} to={`/${c.urlSlug}`}>Online Tuition in {c.name}</FLink>
                        ))}
                    </ul>
                </div>

                {/* Col 5 — Tutor Jobs + Policies */}
                <div>
                    <h4 className="font-heading text-white font-semibold mb-3 text-xs uppercase tracking-[0.2em]">Tutor Jobs</h4>
                    <ul className="space-y-2 text-sm">
                        <FLink to="/home-tutor-jobs-india" testId="footer-tutor-jobs-india">Home Tutor Jobs in India</FLink>
                        <FLink to="/home-tutor-job-jabalpur" testId="footer-tutor-jobs-jabalpur">Jobs · Jabalpur</FLink>
                        <FLink to="/home-tutor-job-bhopal" testId="footer-tutor-jobs-bhopal">Jobs · Bhopal</FLink>
                        <FLink to="/home-tutor-job-nagpur" testId="footer-tutor-jobs-nagpur">Jobs · Nagpur</FLink>
                        <FLink to="/online-tutor-jobs" testId="footer-tutor-jobs-online">Online Tutor Jobs</FLink>
                        <FLink to="/part-time-home-tutor-jobs" testId="footer-tutor-jobs-part-time">Part-Time Tutor Jobs</FLink>
                    </ul>
                    <h4 className="font-heading text-white font-semibold mt-5 mb-3 text-xs uppercase tracking-[0.2em]">Policies</h4>
                    <ul className="space-y-2 text-sm">
                        <FLink to="/policies/terms">Terms &amp; Conditions</FLink>
                        <FLink to="/policies/privacy">Privacy Policy</FLink>
                        <FLink to="/policies/refund">Refund Policy</FLink>
                        <FLink to="/policies/tutor">Tutor Policy</FLink>
                        <FLink to="/policies/parent">Parent Policy</FLink>
                        <FLink to="/policies/fee-and-payment">Fee &amp; Payment</FLink>
                        <FLink to="/policies/gap-and-compensation">Gap &amp; Compensation</FLink>
                        <FLink to="/policies/recontinuation">Recontinuation</FLink>
                    </ul>
                </div>
            </div>

            {/* ═══ TIER 2 — Compact SEO pill row (preserves link equity for
                 every extended metro and country page without making any
                 single column 40 items tall). Branch address cards live in
                 Tier 1 · Col 1 for readability. ═══ */}
            <div className="max-w-[1440px] mx-auto px-6 lg:px-8 pb-6 space-y-4">
                {/* V172 — High-intent keyword-alias row that strengthens
                    internal linking for the three in-person branch cities with
                    the exact-match anchor text users search for on Google
                    ("Home Tutors in Nagpur", "Home Tuition in Bhopal", etc.).
                    Previously Nagpur had fewer internal references than
                    Jabalpur, slightly starving the Nagpur page of link flow.
                    Each pill carries the exact SEO query as anchor text and
                    links to the dedicated city-slug alias route. */}
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">In-Person Home Tuition · Jabalpur · Bhopal · Nagpur</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="footer-branch-keyword-aliases">
                        <Pill to="/jabalpur">Home Tuition in Jabalpur</Pill>
                        <Pill to="/home-tutors-jabalpur">Home Tutors in Jabalpur</Pill>
                        <Pill to="/home-tuition-jabalpur">Home Tuition Jabalpur</Pill>
                        <Pill to="/bhopal">Home Tuition in Bhopal</Pill>
                        <Pill to="/home-tutors-bhopal">Home Tutors in Bhopal</Pill>
                        <Pill to="/home-tuition-bhopal">Home Tuition Bhopal</Pill>
                        <Pill to="/nagpur">Home Tuition in Nagpur</Pill>
                        <Pill to="/home-tutors-nagpur">Home Tutors in Nagpur</Pill>
                        <Pill to="/home-tuition-nagpur">Home Tuition Nagpur</Pill>
                        <Pill to="/anand-tutorials-nagpur">Anand Tutorials Nagpur</Pill>
                    </div>
                </div>
                {/* All metro Online Classes */}
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">All Metro Cities · 1:1 Online Classes</div>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.values(EXTENDED_CITIES).map((c) => (
                            <Pill key={c.slug} to={`/${c.urlSlug}`}>{c.name}</Pill>
                        ))}
                    </div>
                </div>
                {/* All countries */}
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span>Worldwide · 27 Countries</span>
                        <Link to="/online-tuition-worldwide" className="text-amber-400 hover:text-amber-300 normal-case tracking-normal font-semibold inline-flex items-center gap-1">
                            View hub <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.values(COUNTRIES_DATA).map((c) => (
                            <Pill key={c.slug} to={`/${c.urlSlug}`}>
                                <span className="mr-1">{c.flag}</span>{c.name}
                            </Pill>
                        ))}
                        <Pill to="/foreign-students">All NRI / Foreign Students</Pill>
                    </div>
                </div>
            </div>

            {/* Compact app promo — every page footer */}
            <div className="max-w-[1440px] mx-auto px-6 lg:px-8 pb-8">
                <BookMyTutorPromo variant="compact" />
            </div>

            {/* ═══ TIER 3 — Bottom bar ═══ */}
            <div className="border-t border-slate-800 py-5">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
                    <div>© {new Date().getFullYear()} Anand Tutorials. All Rights Reserved.</div>
                    <div className="flex items-center gap-4 flex-wrap justify-center">
                        <Link to="/policies/terms" className="hover:text-amber-400">Terms</Link>
                        <Link to="/policies/privacy" className="hover:text-amber-400">Privacy</Link>
                        <Link to="/policies/refund" className="hover:text-amber-400">Refund</Link>
                        {tutorLoginVisible && (
                            <Link
                                to="/tutor/login"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-700 hover:border-amber-400 hover:text-amber-400 transition-colors"
                                data-testid="footer-tutor-login-link"
                            >
                                <LogIn className="w-3 h-3" /> Tutor Login
                            </Link>
                        )}
                        <Link to="/admin/login" className="hover:text-amber-400" data-testid="footer-admin-link">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
